import type {
  BriefInput,
  Cohort,
  DurationUnit,
  KeyMetric,
  RolloutDraft,
  ValidationIssue,
} from '../types';

/**
 * Draft helpers shared by the Review & Edit sections.
 *
 * Kept as plain functions rather than a store so each section stays a pure
 * component driven by props, which makes the form easy to reason about.
 */

let sequence = 0;
function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${sequence}`;
}

export function newCohort(): Cohort {
  return { id: nextId('cohort'), selected: true, name: '', signals: '', size: '', remarks: '' };
}

export function newMetric(currencySymbol: string): KeyMetric {
  return { id: nextId('metric'), metric: '', benchmark: '', unit: currencySymbol };
}

export function emptyBriefInput(): BriefInput {
  return {
    businessType: 'AGENCY',
    agencyName: '',
    brandName: '',
    channel: '',
    previousBrief: '',
    additionalInfo: '',
  };
}

/**
 * Marks a field as user-edited so the source chip flips from "from brief".
 * Returns a new sources map; never mutates the existing one.
 */
export function markEdited(
  sources: RolloutDraft['sources'],
  path: string,
): RolloutDraft['sources'] {
  if (sources[path] === 'user') return sources;
  return { ...sources, [path]: 'user' };
}

/** Immutably updates a top-level slice of the draft and records the edit. */
export function updateSlice<K extends keyof RolloutDraft>(
  draft: RolloutDraft,
  slice: K,
  patch: Partial<RolloutDraft[K]>,
  editedPaths: string[],
): RolloutDraft {
  let sources = draft.sources;
  for (const path of editedPaths) sources = markEdited(sources, path);

  return {
    ...draft,
    [slice]: { ...(draft[slice] as object), ...patch } as RolloutDraft[K],
    sources,
  };
}

// ---------------------------------------------------------------------------
// Impression calculation — mirrors the backend so the UI updates immediately
// ---------------------------------------------------------------------------

/** Parses a loose numeric string. Returns null when there is no number. */
export function parseNumber(input: string): number | null {
  if (!input) return null;
  const cleaned = input.replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** (RO Value / CPM) x 1,000 — the formula printed under the Impression field. */
export function computeImpressions(roValue: string, cpm: string): string {
  const ro = parseNumber(roValue);
  const cpmValue = parseNumber(cpm);
  if (ro === null || cpmValue === null || cpmValue === 0) return '';
  return String(Math.round((ro / cpmValue) * 1000));
}

/** Thousands separators for display only; the stored value stays unformatted. */
export function formatCount(value: string): string {
  const number = parseNumber(value);
  if (number === null) return value;
  return new Intl.NumberFormat('en-IN').format(number);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * Derives the inclusive end date from a start date and duration, matching the
 * backend so the greyed-out Likely End Date field agrees with the document.
 */
export function deriveEndDate(
  startIso: string,
  durationValue: string,
  unit: DurationUnit,
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startIso.trim());
  const amount = Number(durationValue);
  if (!match || !Number.isFinite(amount) || amount <= 0) return '';

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));

  if (unit === 'DAYS') date.setUTCDate(date.getUTCDate() + amount - 1);
  else if (unit === 'WEEKS') date.setUTCDate(date.getUTCDate() + amount * 7 - 1);
  else {
    const day = date.getUTCDate();
    date.setUTCMonth(date.getUTCMonth() + amount);
    if (date.getUTCDate() < day) date.setUTCDate(0);
    date.setUTCDate(date.getUTCDate() - 1);
  }

  return date.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Validation issue lookup
// ---------------------------------------------------------------------------

export type IssueMap = Record<string, ValidationIssue>;

/** Indexes issues by path, keeping errors ahead of warnings for a given path. */
export function indexIssues(issues: ValidationIssue[]): IssueMap {
  const map: IssueMap = {};
  for (const issue of issues) {
    const existing = map[issue.path];
    if (!existing || (existing.severity === 'warning' && issue.severity === 'error')) {
      map[issue.path] = issue;
    }
  }
  return map;
}

export function errorFor(issues: IssueMap, path: string): string | undefined {
  const issue = issues[path];
  return issue?.severity === 'error' ? issue.message : undefined;
}

export function warningFor(issues: IssueMap, path: string): string | undefined {
  const issue = issues[path];
  return issue?.severity === 'warning' ? issue.message : undefined;
}
